import { PRODUCT_TOKEN, SCORPIUS_KEY } from '@rsos/sinatra/src/actions/types';
import onLogout from '@rsos/utils/onLogout';
import {
  captureExceptionWithScope,
  clearGlobalScope,
} from '@rsos/utils/sentry';
import { notifySessionExpired } from './sessionExpiredNotice';

/** Takes the shared portal logout thunk, which any real store dispatch satisfies. */
type SessionDispatch = Parameters<typeof onLogout>[1];

/**
 * Latches session teardown to once per page load.
 *
 * A single expiry reaches this from several directions at once: every request
 * in flight 401s, and both Hermes clients fail their next connection attempt.
 * Each one arrives here independently, and without this the dispatcher gets one
 * notice and one logout per caller — a pile-up made worse by holding the notice
 * on screen while the next caller arrives.
 */
let teardownStarted = false;

/**
 * Clears the once-per-page teardown latch.
 *
 * Production never needs this: the teardown ends in a navigation, which
 * discards the module. Tests drive the teardown repeatedly in one module
 * instance and would otherwise see only the first case run.
 */
export const resetSessionTeardownForTesting = (): void => {
  teardownStarted = false;
};

/** Whether a teardown already owns this page load. */
export const isSessionTeardownStarted = (): boolean => teardownStarted;

/**
 * Ends the session and sends the user to re-authenticate. Safe to call from
 * anywhere and as often as you like — the first caller owns the teardown and
 * every later one returns immediately.
 *
 * Order matters: notify, then log out, then clear. Wiping storage first means
 * every later step runs against a session that is already unrecoverable, so
 * anything that goes wrong after it strands the user in a tab that still looks
 * signed in and can no longer make a request. Clearing last keeps the bad
 * credential alive only for as long as it takes to get the user out.
 *
 * The notice is awaited rather than fired and forgotten: the navigation
 * otherwise wins the race against the first paint and the user arrives at the
 * login screen with no idea why.
 * @param dispatch - The dispatch of the store that owns this session
 */
export const endSession = async (dispatch: SessionDispatch): Promise<void> => {
  if (teardownStarted) {
    return;
  }
  teardownStarted = true;

  await notifySessionExpired();

  try {
    // The shared portal logout: resets Redux through the sinatra logout action
    // every app's reducers already react to, purges the persisted caches, and
    // navigates to the login screen (/ns for NS SSO sessions).
    await onLogout(undefined, dispatch);
  } catch (error) {
    captureExceptionWithScope(
      error instanceof Error ? error : new Error(`[logout failed] ${error}`),
      { phase: 'auth-teardown' },
      ['auth-teardown', 'logout-error'],
    );
  }

  // Clear the rejected credential last, and unconditionally: it must not
  // survive even when the steps above failed. Clearing the Sentry scope with it
  // stops the next session inheriting this user's context.
  [SCORPIUS_KEY, PRODUCT_TOKEN].forEach(key => localStorage.removeItem(key));
  clearGlobalScope();
};

export default endSession;
